import { useQuery } from "convex/react";
import { api } from "../../../../../convex/_generated/api";
import { useSubscriptionActive } from "../../components/layout/SubscriptionScope";
import { transformOperatorHome } from "../transforms/operatorHome";
import type { OperatorHomeView } from "../../../../../shared/types/operatorHome";

/**
 * HookState discriminated union for Operator Command Center.
 * Discriminant field is `kind` (never `status` or `state`).
 */
export type OperatorHomeState =
  | { kind: "loading" }
  | { kind: "ready"; data: OperatorHomeView }
  | { kind: "error"; error: string }
  | { kind: "forbidden"; role: string };

/**
 * Named React hook for the Operator Command Center home view.
 *
 * Calls useSubscriptionActive() to determine whether to subscribe;
 * passes the literal string "skip" to useQuery when inactive.
 * Maps the raw GetOperatorHomeEnvelope to OperatorHomeState using
 * the kind discriminant.
 */
export function useOperatorHome(): OperatorHomeState {
  const isActive = useSubscriptionActive();

  const envelope = useQuery(
    api.queries.today.getOperatorHome,
    isActive ? {} : "skip",
  );

  // Subscription inactive or query still loading
  if (!isActive || envelope === undefined) {
    return { kind: "loading" };
  }

  // Query resolved but user lacks permission (operatorView is null)
  if (envelope.operatorView === null) {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.debug("[useOperatorHome] forbidden — role=%s", envelope.role);
    }
    return { kind: "forbidden", role: envelope.role };
  }

  // Happy path — apply Number.isFinite guards via transform
  const data = transformOperatorHome(envelope.operatorView);

  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.debug("[useOperatorHome] ready — role=%s", envelope.role);
  }

  return { kind: "ready", data };
}
